import {createInitialFormValues, type CreateJobFormValues} from './types'

export const DEFAULT_STORAGE_KEY = 'mattech:create-job-draft'

export function quoteDraftKey(quoteId: string) {
    return `mattech:job-entry-from-quote:${quoteId}`
}

export function addToOrderDraftKey(projectId: string, sourceJobId: string | null) {
    return `mattech:job-entry-order:${projectId}:${sourceJobId ?? 'order'}`
}

/** Pass on links that should start a fresh wizard (cleared in route beforeLoad). */
export const NEW_WIZARD_SEARCH = {new: 1 as const}

type StoredScheduling = Omit<CreateJobFormValues['scheduling'], 'startDate' | 'endDate'> & {
    startDate: string
    endDate: string
}

type StoredFormValues = Omit<
    CreateJobFormValues,
    'startDate' | 'endDate' | 'scheduling' | 'plannedEndDate'
> & {
    startDate: string | null
    endDate: string | null
    plannedEndDate?: string | null
    scheduling: StoredScheduling
}

type StoredDraft = {
    version: 3
    values: StoredFormValues
    customerName: string | null
    edited?: boolean
}

type CreateJobDraft = {
    values: CreateJobFormValues
    customerName: string | null
    edited: boolean
}

function serializeDate(date: Date | string | null): string | null {
    if (!date) {
        return null
    }
    if (date instanceof Date) {
        return date.toISOString()
    }
    return new Date(date).toISOString()
}

function serializeFormValues(values: CreateJobFormValues): StoredFormValues {
    return {
        ...values,
        startDate: serializeDate(values.startDate),
        endDate: serializeDate(values.endDate),
        plannedEndDate: serializeDate(values.plannedEndDate ?? null),
        scheduling: {
            ...values.scheduling,
            startDate: serializeDate(values.scheduling.startDate)!,
            endDate: serializeDate(values.scheduling.endDate)!,
        },
    }
}

function deserializeFormValues(stored: Partial<StoredFormValues>): CreateJobFormValues {
    const defaults = createInitialFormValues()

    return {
        ...defaults,
        ...stored,
        workerRequirements: stored.workerRequirements ?? defaults.workerRequirements,
        assetRequirements: stored.assetRequirements ?? defaults.assetRequirements,
        projectId: stored.projectId ?? defaults.projectId,
        workMode: stored.workMode ?? (stored.projectId ? 'existing' : defaults.workMode),
        billingTerms: stored.billingTerms ?? defaults.billingTerms,
        cloneJobId: stored.cloneJobId ?? defaults.cloneJobId,
        startDate: stored.startDate ? new Date(stored.startDate) : null,
        endDate: stored.endDate ? new Date(stored.endDate) : null,
        plannedEndDate: stored.plannedEndDate ? new Date(stored.plannedEndDate) : null,
        endTime: null,
        scheduling: stored.scheduling
            ? {
                  ...stored.scheduling,
                  startDate: new Date(stored.scheduling.startDate),
                  endDate: new Date(stored.scheduling.endDate),
                  startTime: stored.scheduling.startTime ?? null,
                  endTime: null,
                  deployEndTime: null,
                  pickupEndTime: null,
                  markJobsReadyForDispatch: stored.scheduling.markJobsReadyForDispatch ?? true,
              }
            : defaults.scheduling,
        locations: stored.locations ?? defaults.locations,
    }
}

export function loadDraft(storageKey = DEFAULT_STORAGE_KEY): CreateJobDraft | null {
    try {
        const raw = sessionStorage.getItem(storageKey)
        if (!raw) {
            return null
        }
        const parsed = JSON.parse(raw) as StoredDraft
        if (parsed.version !== 3 || parsed.values == null || parsed.values.scheduling == null) {
            sessionStorage.removeItem(storageKey)
            return null
        }
        return {
            values: deserializeFormValues(parsed.values),
            customerName: parsed.customerName,
            edited: parsed.edited ?? true,
        }
    } catch {
        return null
    }
}

export function saveDraft(
    values: CreateJobFormValues,
    customerName: string | null,
    storageKey = DEFAULT_STORAGE_KEY,
    edited = true,
) {
    const draft: StoredDraft = {
        version: 3,
        values: serializeFormValues(values),
        customerName,
        edited,
    }
    sessionStorage.setItem(storageKey, JSON.stringify(draft))
}

export function clearDraft(storageKey = DEFAULT_STORAGE_KEY) {
    sessionStorage.removeItem(storageKey)
}

type DuplicateJobSeed = {
    id: string
    customerId: string
    customerName: string
}

/** Clears the create-job draft and seeds customer + clone source for the wizard. */
export function seedDuplicateJobDraft(job: DuplicateJobSeed, storageKey = DEFAULT_STORAGE_KEY) {
    clearDraft(storageKey)
    saveDraft(
        {
            ...createInitialFormValues(),
            customerId: job.customerId,
            cloneJobId: job.id,
        },
        job.customerName,
        storageKey,
        false,
    )
}

/** Last created project and job, stored under its own key next to the draft. */
export type CreatedMark = {
    label: string
    projectId: string
    jobId: string | null
}

function buildCreatedMarkStorageKey(draftKey: string) {
    return `${draftKey}:created`
}

export function saveCreatedMark(draftKey: string, mark: CreatedMark) {
    try {
        sessionStorage.setItem(buildCreatedMarkStorageKey(draftKey), JSON.stringify(mark))
    } catch {
        // Storage can be unavailable (private mode).
    }
}

export function loadCreatedMark(draftKey: string): CreatedMark | null {
    try {
        const raw = sessionStorage.getItem(buildCreatedMarkStorageKey(draftKey))
        return raw ? (JSON.parse(raw) as CreatedMark) : null
    } catch {
        return null
    }
}

export function clearCreatedMark(draftKey: string) {
    try {
        sessionStorage.removeItem(buildCreatedMarkStorageKey(draftKey))
    } catch {
        // Storage can be unavailable (private mode).
    }
}
